import { type FC, useEffect, useRef, useState } from 'react';

import { LoadingIndicator, Stack, Text, toast } from '@eluve/components';
import { useOpenSourceTab } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';

import { ActionItemsSection } from './ActionItemsSection';
import {
  CarePlanEvidenceDrawer,
  type CarePlanEvidenceSelection,
  type CarePlanEvidenceSelectionHandler,
} from './CarePlanEvidenceDrawer';
import { ChecklistSection } from './ChecklistSection';
import { GoalsSection } from './GoalsSection';
import { MeasuresSection } from './MeasuresSection';
import { PhaseSection } from './PhaseSection';
import { ScheduleSection } from './ScheduleSection';
import { TreatingChips } from './TreatingChips';
import { rowProposalIds } from './proposal-rows';
import { type BulkConfirmRow, useCarePlanReview } from './useCarePlanReview';

type ScopedEvidenceSelection = {
  tenantId: string;
  appointmentId: string;
  selection: CarePlanEvidenceSelection;
};

export const CarePlanReviewTab: FC<{
  tenantId: string;
  appointmentId: string;
}> = ({ tenantId, appointmentId }) => {
  const { t } = useTranslation();
  const review = useCarePlanReview({ tenantId, appointmentId });
  const { vm, phase, mutating } = review;
  const { openTranscriptInNewTab, openNoteInNewTab } = useOpenSourceTab();
  const [evidenceSelection, setEvidenceSelection] =
    useState<ScopedEvidenceSelection | null>(null);
  const evidenceTrigger = useRef<HTMLElement | null>(null);
  const activeEvidenceSelection =
    evidenceSelection?.tenantId === tenantId &&
    evidenceSelection.appointmentId === appointmentId
      ? evidenceSelection.selection
      : null;

  useEffect(() => {
    setEvidenceSelection(null);
    evidenceTrigger.current = null;
  }, [tenantId, appointmentId]);

  const selectEvidence: CarePlanEvidenceSelectionHandler = (
    selection,
    triggerElement,
  ) => {
    evidenceTrigger.current = triggerElement;
    setEvidenceSelection({ tenantId, appointmentId, selection });
  };

  const wasComplete = useRef<boolean | null>(null);
  const completed = vm?.checklist.completed ?? false;
  useEffect(() => {
    if (!vm) return;
    if (wasComplete.current === null) {
      wasComplete.current = completed;
      return;
    }

    if (completed && !wasComplete.current) {
      const added = {
        goals: vm.goals.filter(
          (goal) =>
            goal.itemState === 'added' &&
            (goal.decision === 'approved' || goal.decision === 'edited'),
        ).length,
        measures: vm.measures.filter(
          (measure) =>
            measure.thisVisit?.itemState === 'added' &&
            (measure.thisVisit.decision === 'approved' ||
              measure.thisVisit.decision === 'edited'),
        ).length,
        actions: vm.actionItems.filter(
          (item) =>
            item.itemState === 'added' &&
            (item.decision === 'approved' || item.decision === 'edited'),
        ).length,
      };
      const updatedMeasureKeys = new Set(
        vm.measures.flatMap((measure) => {
          const captureUpdated =
            measure.thisVisit?.itemState === 'changed' &&
            (measure.thisVisit.decision === 'approved' ||
              measure.thisVisit.decision === 'edited');
          const targetUpdated = measure.targetChange?.decision === 'approved';
          return captureUpdated || targetUpdated ? [measure.key] : [];
        }),
      );
      const updated = {
        goals: vm.goals.filter(
          (goal) =>
            goal.itemState !== 'added' &&
            (goal.decision === 'edited' ||
              (goal.itemState === 'changed' && goal.decision === 'approved')),
        ).length,
        measures: updatedMeasureKeys.size,
        actions: vm.actionItems.filter(
          (item) =>
            item.itemState !== 'added' &&
            (item.decision === 'edited' ||
              (item.itemState === 'changed' && item.decision === 'approved')),
        ).length,
      };
      const describe = (counts: typeof added) =>
        [
          counts.goals > 0
            ? t(
                counts.goals === 1
                  ? 'carePlans.review.goalCountOne'
                  : 'carePlans.review.goalCountMany',
                { count: counts.goals },
              )
            : null,
          counts.measures > 0
            ? t(
                counts.measures === 1
                  ? 'carePlans.review.measureCountOne'
                  : 'carePlans.review.measureCountMany',
                { count: counts.measures },
              )
            : null,
          counts.actions > 0
            ? t(
                counts.actions === 1
                  ? 'carePlans.review.actionItemCountOne'
                  : 'carePlans.review.actionItemCountMany',
                { count: counts.actions },
              )
            : null,
        ]
          .filter(Boolean)
          .join(' · ');
      const addedDescription = describe(added);
      const updatedDescription = describe(updated);

      toast.success(t('carePlans.review.niceWork'), {
        description: (
          <Stack direction="vertical" gap={0}>
            {addedDescription && (
              <Text>
                {t('carePlans.review.addedSummary', {
                  items: addedDescription,
                })}
              </Text>
            )}
            {updatedDescription && (
              <Text>
                {t('carePlans.review.updatedSummary', {
                  items: updatedDescription,
                })}
              </Text>
            )}
            {!addedDescription && !updatedDescription && (
              <Text>{t('carePlans.review.allChecklistComplete')}</Text>
            )}
          </Stack>
        ),
      });
    }
    wasComplete.current = completed;
  }, [completed, vm, t]);

  if (phase === 'error') {
    return (
      <Text size="sm" color="secondary" className="block py-8 text-center">
        {t('carePlans.review.loadFailed')}
      </Text>
    );
  }
  if (phase === 'loading' || !vm) {
    return (
      <Stack direction="vertical" align="center" className="py-12">
        <LoadingIndicator />
      </Stack>
    );
  }

  const decideRow = (
    itemType: 'goal' | 'action-item',
    row: {
      id: string;
      pendingChanges: (typeof vm.goals)[number]['pendingChanges'];
    },
    action: 'confirm' | 'reject' | 'undo',
  ) => {
    const proposalIds = rowProposalIds(row, action);
    if (proposalIds.length > 0) {
      void review.decidePlanItemProposals({
        itemType,
        id: row.id,
        action,
        proposalIds,
      });
    } else {
      void review.decidePlanItem({ itemType, id: row.id, action });
    }
  };

  const goalVerdict =
    (action: 'confirm' | 'reject' | 'undo') =>
    (goal: (typeof vm.goals)[number]) => {
      if (goal.findingBacked) {
        if (action === 'undo') void review.undoFinding('Goal', goal.id);
        else
          void review.decideFinding(
            'Goal',
            goal.id,
            action === 'confirm' ? 'approve' : 'reject',
          );
        return;
      }
      decideRow('goal', goal, action);
    };

  const actionItemVerdict =
    (action: 'confirm' | 'reject' | 'undo') =>
    (item: (typeof vm.actionItems)[number]) =>
      decideRow('action-item', item, action);

  const goalBulkRow = (goal: (typeof vm.goals)[number]): BulkConfirmRow =>
    goal.findingBacked
      ? { kind: 'finding', resourceType: 'Goal', id: goal.id }
      : {
          kind: 'item',
          itemType: 'goal',
          id: goal.id,
          proposalIds: rowProposalIds(goal, 'confirm'),
        };

  const measureBulkRows = (measures: typeof vm.measures): BulkConfirmRow[] =>
    measures.flatMap((measure) => {
      const rows: BulkConfirmRow[] = [];
      if (measure.thisVisit && measure.thisVisit.itemState !== 'unchanged') {
        rows.push({
          kind: 'finding',
          resourceType: 'Observation',
          id: measure.thisVisit.observationId,
        });
      }
      if (measure.targetChange?.decision === 'pending') {
        rows.push({
          kind: 'item',
          itemType: 'goal',
          id: measure.targetChange.goalId,
          proposalIds: [measure.targetChange.proposalId],
        });
      }
      return rows;
    });

  return (
    <Stack direction="vertical" gap={4}>
      <ChecklistSection
        checklist={vm.checklist}
        plan={vm.plan}
        disabled={mutating}
        onConfirmCarePlan={() => void review.activate()}
      />
      {vm.plan && (
        <>
          <PhaseSection
            phase={vm.phase}
            startDate={vm.plan.startDate}
            disabled={mutating}
            onSelectPhase={(n) => void review.setPlanPhase(n)}
          />
          <ScheduleSection
            schedule={vm.schedule}
            disabled={mutating}
            onSetSchedule={(args) => void review.setSchedule(args)}
          />
        </>
      )}
      <TreatingChips treating={vm.treating ?? []} />
      <GoalsSection
        goals={vm.goals}
        disabled={mutating}
        showBulkUndo={Boolean(review.bulkConfirmed.goals?.length)}
        onConfirm={goalVerdict('confirm')}
        onConfirmAll={(goals) =>
          void review.confirmAll('goals', goals.map(goalBulkRow))
        }
        onUndoConfirmAll={() => void review.undoConfirmAll('goals')}
        onReject={goalVerdict('reject')}
        onUndo={goalVerdict('undo')}
        onEditGoal={(g, fields) => {
          if (g.findingBacked) {
            void review.editFinding('Goal', g.id, {
              display: fields.text,
              targetDate: fields.targetDate,
              done: fields.done,
              isPrimary: fields.isPrimary,
            });
          } else {
            void review.editPlanItem({
              itemType: 'goal',
              id: g.id,
              text: fields.text,
              targetDate: fields.targetDate,
              done: fields.done,
              isPrimary: fields.isPrimary,
              implicitConfirm: g.itemState !== 'unchanged',
            });
          }
        }}
        onDelete={(g) => {
          if (g.findingBacked) {
            void review.dismissFinding('Goal', g.id);
          } else {
            void review.decidePlanItem({
              itemType: 'goal',
              id: g.id,
              action: 'delete',
            });
          }
        }}
        onAddGoal={(fields) => void review.addGoal(fields)}
        onSelectEvidence={selectEvidence}
      />
      <MeasuresSection
        measures={vm.measures}
        disabled={mutating}
        showBulkUndo={Boolean(review.bulkConfirmed.measures?.length)}
        onConfirm={(m) => void review.decideMeasureRow(m, 'approve')}
        onConfirmAll={(measures) =>
          void review.confirmAll('measures', measureBulkRows(measures))
        }
        onUndoConfirmAll={() => void review.undoConfirmAll('measures')}
        onReject={(m) => void review.decideMeasureRow(m, 'reject')}
        onUndo={(m) => void review.decideMeasureRow(m, 'undo')}
        onEditValue={(m, value) =>
          m.thisVisit &&
          void review.editPlanItem({
            itemType: 'measure',
            id: m.thisVisit.observationId,
            value,
            stringValue: null,
            unit: m.thisVisit.unit,
            implicitConfirm: m.thisVisit.itemState !== 'unchanged',
          })
        }
        onEditMeasure={(m, fields) => {
          if (!m.thisVisit && !m.target) return;
          void review.editPlanItem({
            itemType: 'measure',
            id: m.thisVisit?.observationId ?? m.key,
            observationId: m.thisVisit?.observationId ?? null,
            ...(m.target ? { measureCode: m.target.measureCode } : {}),
            value: m.thisVisit?.value ?? null,
            stringValue: m.thisVisit?.stringValue ?? null,
            unit: fields.targetUnit ?? m.thisVisit?.unit ?? null,
            name: fields.name,
            targetValue: fields.targetValue,
            targetUnit: fields.targetUnit,
            direction: fields.direction,
            implicitConfirm:
              m.thisVisit?.itemState !== undefined &&
              m.thisVisit.itemState !== 'unchanged',
          });
        }}
        onDelete={(m) => {
          if (!m.thisVisit && !m.target) return;
          void review.decidePlanItem({
            itemType: 'measure',
            id: m.thisVisit?.observationId ?? m.key,
            observationId: m.thisVisit?.observationId ?? null,
            ...(m.target ? { measureCode: m.target.measureCode } : {}),
            action: 'delete',
          });
        }}
        onAddMeasure={(fields) => void review.addMeasure(fields)}
        onSelectEvidence={selectEvidence}
      />
      <ActionItemsSection
        actionItems={vm.actionItems}
        disabled={mutating}
        showBulkUndo={Boolean(review.bulkConfirmed['action-items']?.length)}
        onConfirm={actionItemVerdict('confirm')}
        onConfirmAll={(items) =>
          void review.confirmAll(
            'action-items',
            items.map(
              (item): BulkConfirmRow => ({
                kind: 'item',
                itemType: 'action-item',
                id: item.id,
                proposalIds: rowProposalIds(item, 'confirm'),
              }),
            ),
          )
        }
        onUndoConfirmAll={() => void review.undoConfirmAll('action-items')}
        onReject={actionItemVerdict('reject')}
        onUndo={actionItemVerdict('undo')}
        onEditText={(item, text) =>
          void review.editPlanItem({
            itemType: 'action-item',
            id: item.id,
            text,
            implicitConfirm: item.itemState !== 'unchanged',
          })
        }
        onDelete={(item) =>
          void review.decidePlanItem({
            itemType: 'action-item',
            id: item.id,
            action: 'delete',
          })
        }
        onAdd={(text) => void review.addActionItem(text)}
        onSelectEvidence={selectEvidence}
      />
      <CarePlanEvidenceDrawer
        tenantId={tenantId}
        currentAppointmentId={appointmentId}
        selection={activeEvidenceSelection}
        triggerElement={
          activeEvidenceSelection ? evidenceTrigger.current : null
        }
        onClose={() => setEvidenceSelection(null)}
        onOpenSource={(target) =>
          target === 'transcript'
            ? openTranscriptInNewTab()
            : openNoteInNewTab()
        }
      />
    </Stack>
  );
};
